import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Power, Pencil, KeyRound } from "lucide-react";
import { toast } from "sonner";

const ROLES_SUCURSAL = ["ADMIN_SUCURSAL", "MESERO", "COCINA", "BEBIDAS", "FREIDORA", "CAJA", "REPARTIDOR"];
const ROLES_CON_AREAS = ["COCINA", "BEBIDAS", "FREIDORA"];

function UserDialog({ open, onOpenChange, target, sucursales, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [areas, setAreas] = useState([]);
  const editing = !!target?.id;
  useEffect(() => {
    if (open) {
      const baseSids = target?.sucursales_ids?.length
        ? target.sucursales_ids
        : (target?.sucursal_id ? [target.sucursal_id] : []);
      setForm({
        nombre: target?.nombre || "",
        email: target?.email || "",
        role: target?.role || "MESERO",
        password: "",
        sucursales_ids: baseSids,
        areas_ids: target?.areas_ids || [],
      });
      // Cargar áreas de la sucursal activa para el selector
      api.get(`/areas?only_active=true`).then((r) => setAreas(r.data || [])).catch(() => setAreas([]));
    }
  }, [open, target]);

  const toggleSucursal = (id) => {
    const cur = form.sucursales_ids || [];
    setForm({
      ...form,
      sucursales_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
    });
  };

  const toggleArea = (id) => {
    const cur = form.areas_ids || [];
    setForm({
      ...form,
      areas_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
    });
  };

  const save = async () => {
    if (!form.nombre?.trim()) return toast.error("Nombre obligatorio");
    if (!editing) {
      if (!form.email?.trim()) return toast.error("Email obligatorio");
      if (!form.password) return toast.error("Contraseña obligatoria");
    }
    setSaving(true);
    try {
      if (editing) {
        const payload = { nombre: form.nombre.trim(), role: form.role };
        if (form.role === "ADMIN_SUCURSAL") payload.sucursales_ids = form.sucursales_ids;
        else if (form.sucursales_ids?.length === 1) payload.sucursal_id = form.sucursales_ids[0];
        if (ROLES_CON_AREAS.includes(form.role)) payload.areas_ids = form.areas_ids || [];
        await api.patch(`/auth/users/${target.id}`, payload);
        toast.success(`Usuario "${form.nombre}" actualizado correctamente.`);
      } else {
        const payload = {
          nombre: form.nombre.trim(),
          email: form.email.toLowerCase().trim(),
          role: form.role,
          password: form.password,
        };
        if (form.role === "ADMIN_SUCURSAL") {
          if (!form.sucursales_ids?.length) {
            setSaving(false);
            return toast.error("Asigna al menos una sucursal");
          }
          payload.sucursales_ids = form.sucursales_ids;
        } else if (form.sucursales_ids?.length) {
          payload.sucursal_id = form.sucursales_ids[0];
        }
        if (ROLES_CON_AREAS.includes(form.role) && form.areas_ids?.length) {
          payload.areas_ids = form.areas_ids;
        }
        await api.post("/auth/users", payload);
        toast.success("Usuario creado correctamente.");
      }
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{editing ? "Editar usuario" : "Nuevo usuario"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nombre *</Label>
            <Input value={form.nombre || ""} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="usr-nombre-input" />
          </div>
          <div>
            <Label>Email {editing ? "(no se puede cambiar)" : "*"}</Label>
            <Input type="email" value={form.email || ""} disabled={editing} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="usr-email-input" />
          </div>
          <div>
            <Label>Rol *</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="usr-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES_SUCURSAL.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {!editing && (
            <div>
              <Label>Contraseña *</Label>
              <Input type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="usr-password-input" />
              <p className="text-[10px] text-muted-foreground mt-1">Min 8: mayúscula, minúscula, número y símbolo.</p>
            </div>
          )}
          {/* Multi-sucursal solo si es ADMIN_SUCURSAL y hay más de una sucursal */}
          {form.role === "ADMIN_SUCURSAL" && sucursales.length > 1 && (
            <div>
              <Label>Sucursales asignadas *</Label>
              <div className="mt-2 space-y-1 max-h-44 overflow-y-auto rounded-md border border-border p-2">
                {sucursales.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-secondary/50 cursor-pointer">
                    <Checkbox
                      checked={(form.sucursales_ids || []).includes(s.id)}
                      onCheckedChange={() => toggleSucursal(s.id)}
                      data-testid={`usr-suc-${s.id}`}
                    />
                    <span className="text-sm">{s.nombre}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Áreas asignadas: para roles operativos (multi-área) */}
          {ROLES_CON_AREAS.includes(form.role) && (
            <div>
              <Label>Áreas asignadas</Label>
              <p className="text-[10px] text-muted-foreground mt-0.5 mb-1.5">
                Marca una o varias áreas. El usuario verá los pedidos de todas en una sola pantalla.
              </p>
              {areas.length === 0 ? (
                <div className="text-xs text-muted-foreground py-2">No hay áreas activas en esta sucursal.</div>
              ) : (
                <div className="mt-1 grid grid-cols-2 gap-1 max-h-48 overflow-y-auto rounded-md border border-border p-2">
                  {areas.map((a) => (
                    <label key={a.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-secondary/50 cursor-pointer">
                      <Checkbox
                        checked={(form.areas_ids || []).includes(a.id)}
                        onCheckedChange={() => toggleArea(a.id)}
                        data-testid={`usr-area-${a.nombre.toLowerCase()}`}
                      />
                      <span className="text-sm font-semibold">{a.nombre}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="usr-save-btn">{saving ? "Guardando…" : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PasswordDialog({ open, onOpenChange, target, onSaved }) {
  const [pwd, setPwd] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setPwd(""); }, [open]);

  const save = async () => {
    if (!pwd) return toast.error("Ingresa nueva contraseña");
    setSaving(true);
    try {
      await api.patch(`/auth/users/${target.id}`, { password: pwd });
      toast.success("Contraseña actualizada correctamente.");
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Cambiar contraseña — {target?.nombre}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nueva contraseña</Label>
            <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} autoFocus data-testid="new-pwd-input" />
            <p className="text-[10px] text-muted-foreground mt-1">Min 8: mayúscula, minúscula, número y símbolo.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="pwd-save-btn">{saving ? "Guardando…" : "Cambiar contraseña"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Usuarios() {
  const { sucursales } = useAuth();
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const load = () => api.get("/auth/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("sucursal-changed", h);
    return () => window.removeEventListener("sucursal-changed", h);
  }, []);

  const toggle = async (u) => {
    try {
      if (u.active) {
        await api.delete(`/auth/users/${u.id}`);
        toast.success(`Usuario "${u.nombre}" desactivado correctamente.`);
      } else {
        await api.post(`/auth/users/${u.id}/activar`);
        toast.success(`Usuario "${u.nombre}" reactivado correctamente.`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const visible = users.filter((u) => showInactive || u.active);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div>
          <h2 className="text-3xl font-black tracking-tight">Usuarios</h2>
          <p className="text-sm text-muted-foreground mt-1">Gestiona el equipo operativo de tu sucursal.</p>
        </div>
        <div className="flex gap-2 items-center">
          <label className="text-xs flex items-center gap-2 cursor-pointer">
            <Checkbox checked={showInactive} onCheckedChange={setShowInactive} data-testid="show-inactive-check" />
            Mostrar inactivos
          </label>
          <Button onClick={() => { setTarget(null); setDialogOpen(true); }} data-testid="new-user-btn">
            <Plus className="h-4 w-4 mr-1" />Nuevo usuario
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Sin usuarios.</TableCell></TableRow>
              ) : visible.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-semibold">{u.nombre}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell><span className="px-2 py-1 rounded text-xs font-bold bg-secondary">{u.role}</span></TableCell>
                  <TableCell>
                    {u.active
                      ? <span className="text-emerald-500 font-bold text-xs">ACTIVO</span>
                      : <span className="text-red-500 font-bold text-xs">INACTIVO</span>}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => { setTarget(u); setDialogOpen(true); }} data-testid={`edit-user-${u.id}`}>
                      <Pencil className="h-3 w-3 mr-1" />Editar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setTarget(u); setPwdOpen(true); }} data-testid={`pwd-user-${u.id}`}>
                      <KeyRound className="h-3 w-3 mr-1" />Contraseña
                    </Button>
                    <Button size="sm" variant={u.active ? "ghost" : "default"} onClick={() => toggle(u)} data-testid={`toggle-user-${u.id}`}>
                      <Power className="h-3 w-3 mr-1" />{u.active ? "Desactivar" : "Activar"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <UserDialog open={dialogOpen} onOpenChange={setDialogOpen} target={target} sucursales={sucursales || []} onSaved={load} />
      <PasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} target={target} onSaved={load} />
    </div>
  );
}
